import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { showSuccess, showError } from "@/utils/toast";
import { useArtieScore } from "@/hooks/use-artie-score";
import { useInventoryMode } from "@/hooks/useInventoryMode";
import { useCollaboration } from "@/hooks/use-collaboration";
import { useAuthStore } from "@/stores/authStore";
import { Skeleton } from "@/components/ui/skeleton";
import { AnalysisLoader } from "@/components/AnalysisLoader";
import { Input } from "@/components/ui/input";
import { ShareScoreButton } from "@/components/score/ShareScoreButton";
import { RecommendationsSection } from "@/components/score/RecommendationsSection";
import {
  Users,
  Target,
  Film,
  Sparkles,
  RotateCw,
  MoreVertical,
  Pencil,
  Check,
  X
} from "lucide-react";
import { format } from "date-fns";
import logo from '../assets/images/logo-artie.png';
import { useState, useEffect } from "react";

export default function ProjectDetailsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  // The landing page for the /projects/new upload flow, and the most reachable score screen in the
  // app — the dashboard cards and the project list both point here. In inventory mode there is no
  // score to show, so a creator who just uploaded belongs on the acknowledgement instead.
  const inventoryMode = useInventoryMode();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => apiClient.getProject(projectId!),
    enabled: !!projectId,
  });

  // Use collaboration hook for permission checks
  const { isOwner, canEdit, isLoading: collabLoading } = useCollaboration(
    projectId || "",
    user?._id || ""
  );

  // Kept as its own effect, ahead of the pending-page logic below, because that logic is gated on
  // VITE_SHOW_PENDING_PAGE — which is committed as 'false', the branch that deliberately stays on
  // this page. So the existing redirect could not be relied on to carry inventory mode.
  useEffect(() => {
    if (inventoryMode) {
      navigate(projectId ? `/upload-thanks?projectId=${projectId}` : "/upload-thanks", {
        replace: true,
      });
    }
  }, [inventoryMode, projectId, navigate]);

  useEffect(() => {
    // Do not fight the redirect above, and do not act before the mode is known.
    if (inventoryMode !== false) return;
    if (project) {
      setEditedName(project.title);

      const showPendingPageEnv = import.meta.env.VITE_SHOW_PENDING_PAGE;

      if (showPendingPageEnv === 'true' || showPendingPageEnv === 'false') {
        if (showPendingPageEnv === 'true') {
          navigate(`/projects/${projectId}/pending`);
        } else {
          // We don't force redirection to score, just don't block it.
          // The page will show the score details if available.
        }
      } else {
        // VITE_SHOW_PENDING_PAGE is not set, or is an empty string, null, or undefined.
        // Give priority to `is_reviewed`.
        if (!project.is_reviewed) {
          navigate(`/projects/${projectId}/pending`);
        }
      }
    }
  }, [project, projectId, navigate, inventoryMode]);

  // Update project name mutation
  const updateProjectMutation = useMutation({
    mutationFn: (newTitle: string) => apiClient.updateProject(projectId!, { title: newTitle }),
    onSuccess: () => {
      showSuccess("Project name updated successfully");
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      setIsEditingName(false);
    },
    onError: (error: Error) => {
      showError(error.message || "Failed to update project name");
    },
  });

  const handleSaveName = () => {
    if (editedName.trim() && editedName !== project?.title) {
      updateProjectMutation.mutate(editedName);
    } else {
      setIsEditingName(false);
      setEditedName(project?.title || "");
    }
  };


  // Use the hook that generates/fetches the score
  const {
    scoreData: artieScore,
    loading: scoreLoading,
    error: scoreError,
    currentStep,
    statusMessage,
    progressPercent,
    refetch: refetchScore,
    refresh: refreshScore,
  } = useArtieScore(projectId);

  // Invalidate project query when score data changes to update logline and status
  useEffect(() => {
    if (artieScore && !scoreLoading) {
      // Invalidate the project query to refetch updated project data
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    }
  }, [artieScore, scoreLoading, projectId, queryClient]);

  // Calculate score values or use defaults
  const scoreValue = artieScore?.artie_score || 0;
  const appliedBonus = artieScore?.applied_bonus || 0;
  const scoreTone = artieScore?.score_tone || "Upload a script to generate your score.";
  
  // Extract logline and genre from score data
  const extractedGenre = artieScore?.brand_fit_analysis?.category_mapping?.genre;
  const extractedLogline = project?.logline; // Logline comes from project, not score

  const getProjectStatus = (score: number) => {
    if (score >= 80) return { label: "On track", color: "text-green-500", ringColor: "text-green-500" };
    if (score >= 60) return { label: "Needs work", color: "text-yellow-500", ringColor: "text-yellow-500" };
    return { label: "At risk", color: "text-red-500", ringColor: "text-red-500" };
  };

  const projectStatus = getProjectStatus(scoreValue);
  
  // Reach analysis
  const reachAnalysis = artieScore?.reach_analysis;
  const reachStatus = artieScore?.reach_status || "Pending";
  const reachColor = (() => {
    const s = (reachStatus || "").toLowerCase();
    if (s === "green" || s === "high") return "green";
    if (s === "yellow" || s === "medium") return "yellow";
    if (s === "red" || s === "low") return "red";
    return "gray";
  })();
  const reachText = reachAnalysis?.gap_analysis
    ? reachAnalysis.gap_analysis
    : (reachAnalysis?.sufficient_for_target
        ? "Projected reach is sufficient for your target budget."
        : "Audience growth needed to meet budget target.");

  // Brand fit
  const brandFitAnalysis = artieScore?.brand_fit_analysis;
  const brandFitStatus = artieScore?.brand_fit_status || "Pending";
  const brandFitColor = (() => {
    const s = (brandFitStatus || "").toLowerCase();
    if (s === "green" || s === "high") return "green";
    if (s === "yellow" || s === "medium") return "yellow";
    if (s === "red" || s === "low") return "red";
    return "gray";
  })();
  
  const brandFitText = (() => {
    if (!brandFitAnalysis) return "Brand fit analysis pending.";
    if (brandFitStatus.toLowerCase() === 'green') return `Strong alignment with ${brandFitAnalysis.accessible_brand_pool || 'multiple'} potential sponsors.`;
    if (brandFitStatus.toLowerCase() === 'yellow') return "Moderate fit. Some flexibility adjustments could unlock more brands.";
    return "Limited brand pool due to safety or category constraints.";
  })();

  // Production readiness
  const prodReadinessAnalysis = artieScore?.production_readiness_analysis;
  const prodReadinessStatus = artieScore?.production_readiness_status || "Pending";
  const prodReadinessColor = (() => {
    const s = (prodReadinessStatus || "").toLowerCase();
    if (s === "green" || s === "ready" || s === "high") return "green";
    if (s === "yellow" || s === "needs work" || s === "medium") return "yellow";
    if (s === "red" || s === "low") return "red";
    return "gray";
  })();
  
  const prodReadinessText = (() => {
    if (!prodReadinessAnalysis) return "Readiness analysis pending.";
    const gapCount = prodReadinessAnalysis.gaps?.length || 0;
    if (prodReadinessStatus.toLowerCase() === 'green') return "Script is production-ready.";
    if (gapCount > 0) return `${gapCount} key gaps identified in script craft or planning.`;
    return "Script requires further development.";
  })();

  // Artie's Advice Logic
  const artieAdvice = (() => {
    if (!artieScore) return "I've analyzed your script and projected your potential reach. Review the score breakdown below to see how we can optimize your project for the market.";
    
    // Check for "Red" statuses first (High priority)
    if (reachColor === 'red') return "I’d focus on Reach first. Your current plan makes it hard to hit your goal. Add a marketing plan to improve your status.";
    if (brandFitColor === 'red') return "I'd focus on Brand Fit first. Your content might be too risky for most sponsors. Consider adjusting the tone or themes.";
    if (prodReadinessColor === 'red') return "I'd focus on Production Readiness. The script needs significant work before it's ready for production.";

    // Check for "Yellow" statuses (Medium priority)
    if (reachColor === 'yellow') return "Your Reach is okay, but could be better. A stronger distribution plan would help move you to 'On Track'.";
    if (brandFitColor === 'yellow') return "Brand Fit is decent, but expanding your category appeal could unlock more budget.";
    if (prodReadinessColor === 'yellow') return "The script is getting there, but a few key gaps remain. addressing them will improve your score.";

    // If all Green
    if (scoreValue >= 80) return "Great work! Your project is On Track. Check out the specific recommendations to polish it further.";

    return "I've analyzed your script. Review the breakdown to see where we can optimize.";
  })();


  // Show skeleton loader for project loading
  if (projectLoading) {
    return <ProjectDetailsSkeleton />;
  }

  // Handle score loading state
  if (scoreLoading) {
    // Always show AnalysisLoader when loading/recalculating score
    // This ensures users see the loading animation during refresh
    return <AnalysisLoader currentStep={currentStep} statusMessage={statusMessage} projectId={projectId} progressPercent={progressPercent} />;
  }

  // Handle score calculation errors (e.g. Validation Errors)
  if (scoreError) {
      // If it's a validation error, show a friendly message
      if (scoreError.includes("Script Validation Failed")) {
          return (
              <div className="flex flex-col items-center justify-center h-[50vh] p-8 max-w-2xl mx-auto text-center animate-in fade-in zoom-in duration-300">
                  <div className="p-4 bg-orange-50 rounded-full mb-6 ring-8 ring-orange-50/50">
                      <Sparkles className="w-12 h-12 text-orange-500" />
                  </div>
                  <h2 className="text-2xl font-bold font-cabinet text-foreground mb-3">Script Needs Improvement</h2>
                  <p className="text-muted-foreground mb-8 leading-relaxed">
                      {scoreError.replace("Validation error: ", "")}
                  </p>
                  
                  <div className="flex gap-4">
                      <button
                          onClick={() => navigate("/dashboard")}
                          className="px-6 py-2.5 bg-secondary text-secondary-foreground border border-border rounded-xl font-medium hover:bg-secondary/80 transition-all"
                      >
                          Back to Dashboard
                      </button>
                      <button
                          onClick={() => navigate("/projects/new")}
                          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium hover:opacity-90 transition-all"
                      >
                          Create New Project
                      </button>
                  </div>
              </div>
          );
      }
  }

  if (projectError || !project) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] p-4">
        <h1 className="text-2xl font-bold mb-4">Project Not Found</h1>
        <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
        >
            Back to Dashboard
        </button>
      </div>
    );
  }

  // Calculate SVG dash array for score circle
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const progress = scoreValue / 100;
  const dashoffset = circumference - progress * circumference;

  return (
    <>
      {/* Project Name Header */}
      <div className="flex items-center gap-4 mb-6 animate-fade-in">
        {isEditingName && isOwner ? (
          <div className="flex items-center gap-2 flex-1 max-w-xl">
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="text-lg md:text-4xl font-bold font-cabinet h-14 bg-background"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') {
                  setIsEditingName(false);
                  setEditedName(project.title);
                }
              }}
            />
            <button
              onClick={handleSaveName}
              disabled={updateProjectMutation.isPending}
              className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-full transition-colors"
            >
              <Check className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                setIsEditingName(false);
                setEditedName(project.title);
              }}
              className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 group">
            <h1 className="text-lg md:text-4xl font-bold font-cabinet text-[var(--foreground)] tracking-tight">
              {project.title}
            </h1>
            {isOwner && (
              <button
                onClick={() => setIsEditingName(true)}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-2 hover:bg-secondary rounded-full text-muted-foreground hover:text-foreground"
                title="Edit project name"
              >
                <Pencil className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

      </div>

      {/* Project Metadata Section - Read-only critical inputs */}
      <div className="w-full mb-6 p-5 bg-gradient-to-br from-secondary/30 to-secondary/10 border border-border rounded-2xl animate-fade-in">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
          <h2 className="text-sm font-semibold text-foreground font-cabinet uppercase tracking-wide">
            Project Inputs
          </h2>
          <span className="text-xs text-muted-foreground font-ibm ml-auto">
            These inputs influence your score
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Logline - always show (from project) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
              Logline
            </label>
            <p className="text-sm text-foreground font-ibm leading-relaxed line-clamp-2">
              {extractedLogline || "Analyzing script..."}
            </p>
          </div>

          {/* Genre - always show (extracted from score analysis) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
              Genre
            </label>
            <p className="text-sm text-foreground font-ibm">
              {extractedGenre || "Analyzing script..."}
            </p>
          </div>

          {/* Budget Target - only show if exists */}
          {project.brand_revenue_target && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
                Budget Target
              </label>
              <p className="text-sm text-foreground font-ibm font-semibold">
                ${project.brand_revenue_target.toLocaleString()}
              </p>
            </div>
          )}

          {/* Script Status - always show */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
              Script Status
            </label>
            <p className="text-sm text-foreground font-ibm capitalize">
              {project.status === 'analysis_complete' ? 'Analyzed' : formatStatus(project.status)}
            </p>
          </div>

          {/* Format - only show if exists */}
          {project.format && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
                Format
              </label>
              <p className="text-sm text-foreground font-ibm">
                {project.format}
              </p>
            </div>
          )}

          {/* Flexibility - only show if exists */}
          {project.flexibility_preference && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
                Brand Flexibility
              </label>
              <p className="text-sm text-foreground font-ibm capitalize">
                {project.flexibility_preference.replace(/_/g, " ")}
              </p>
            </div>
          )}

          {/* Launch Date - only show if exists */}
          {project.launch_date && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
                Expected Release
              </label>
              <p className="text-sm text-foreground font-ibm">
                {format(new Date(project.launch_date), "MMM d, yyyy")}
              </p>
            </div>
          )}

          {/* Target Reach - only show if exists */}
          {project.target_reach && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-ibm">
                Target Reach
              </label>
              <p className="text-sm text-foreground font-ibm">
                {project.target_reach}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Hero Grid: Info + Score */}
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Artie Insight Card */}
          <div className="md:col-span-2 p-6 md:p-8 bg-[var(--card)] border border-[var(--border)] rounded-2xl flex flex-col justify-between animate-slide-up relative overflow-hidden group">
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[var(--secondary)] to-transparent opacity-50 rounded-bl-[100px] -z-0 pointer-events-none transition-opacity group-hover:opacity-70"></div>
            
            <div className="flex items-start gap-6 relative z-10">
              {/* Artie Avatar */}
              <div className="relative shrink-0 hidden sm:block">
                <div className="w-20 h-20 rounded-full bg-white border-4 border-white shadow-lg overflow-hidden relative z-10">
                    <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                </div>
                {/* Status Badge attached to Artie */}
              </div>

              {/* Content */}
              <div className="flex flex-col items-start gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-between w-full">
                  <div className="flex flex-col">
                    <h1 className="text-xl md:text-2xl font-bold font-cabinet text-[var(--foreground)] tracking-tight">
                       Hi, I'm Artie!
                    </h1>
                    <span className="text-xs text-[var(--muted-foreground)] font-ibm">
                      AI Content Strategist
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                     <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[var(--secondary)]/50 border border-[var(--border)]">
                        <span className={`w-1.5 h-1.5 rounded-full ${project.status === 'analysis_complete' ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}></span>
                        <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                          {project.status === 'analysis_complete' ? 'Analysis Ready' : formatStatus(project.status)}
                        </span>
                      </div>
                  </div>
                </div>

                {/* Speech Bubble Style Content */}
                <div className="relative bg-[var(--secondary)]/30 p-4 rounded-xl rounded-tl-sm border border-[var(--border)] w-full">
                   <p className="text-[var(--foreground)] font-ibm text-sm leading-relaxed">
                      {artieAdvice}
                   </p>
                </div>
              </div>
            </div>
          </div>

          {/* Score Card - Project Status Focus */}
          <div className="md:col-span-1 p-6 bg-[var(--foreground)] text-[var(--background)] rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden shadow-xl animate-slide-up delay-100 group">
            <div className="absolute inset-0 opacity-10 bg-white/5"></div>

            <div className="relative z-10 w-full flex flex-col items-center h-full justify-between">
              <div className="flex justify-between w-full items-center mb-4">
                <span className="text-xs font-medium opacity-70 font-ibm uppercase tracking-wider">Project Status</span>
                <MoreVertical className="w-4 h-4 opacity-50" />
              </div>

              {/* Status Display */}
              <div className="flex flex-col items-center gap-4 my-auto">
                 <h2 className={`text-4xl font-bold font-cabinet tracking-tight ${
                    scoreValue >= 80 ? 'text-green-400' :
                    scoreValue >= 60 ? 'text-yellow-400' : 'text-red-400'
                 }`}>
                    {projectStatus.label}
                 </h2>
                 <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full border border-white/10">
                    <div className={`w-2 h-2 rounded-full ${
                        scoreValue >= 80 ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]' :
                        scoreValue >= 60 ? 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.5)]' : 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.5)]'
                    }`}></div>
                    <span className="text-sm font-medium font-ibm">
                      {appliedBonus > 0 ? (
                        <>
                          <span>{scoreValue - appliedBonus}</span>
                          <span className="text-green-400 font-bold ml-1">+{appliedBonus}</span>
                          <span> / 100 Score</span>
                        </>
                      ) : (
                        `${scoreValue}/100 Score`
                      )}
                    </span>
                 </div>
              </div>

              <div className="mt-4 text-center">
                <p className="text-xs opacity-70 font-ibm leading-relaxed max-w-[200px] mx-auto">
                   {scoreTone}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Traffic Light Cards - High Level Previews */}
        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 animate-slide-up delay-200">
          
          {/* Card 1: Reach */}
          <div className="relative h-full group">
            <Link to={`/projects/${projectId}/reach`} className="block relative p-5 pt-6 bg-[var(--card)] border border-[var(--border)] rounded-2xl hover:border-[var(--primary)]/50 hover:shadow-lg transition-all text-left flex flex-col gap-3 overflow-hidden h-full">
              <div className="flex justify-between items-start z-10 w-full">
              <div className="p-2 rounded-lg bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)] shadow-sm">
                <Users className="w-4.5 h-4.5" />
              </div>
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                  reachColor === 'green' ? 'bg-green-500/10 border-green-500/20' :
                  reachColor === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20' :
                  reachColor === 'red' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-gray-500/10 border-gray-500/20'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                    reachColor === 'green' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' :
                    reachColor === 'yellow' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]' :
                    reachColor === 'red' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' :
                    'bg-gray-500'
                }`}></div>
                <span className={`text-[10px] font-bold uppercase tracking-wide ${
                    reachColor === 'green' ? 'text-green-700 dark:text-green-400' :
                    reachColor === 'yellow' ? 'text-yellow-700 dark:text-yellow-400' :
                    reachColor === 'red' ? 'text-red-700 dark:text-red-400' :
                    'text-gray-700 dark:text-gray-400'
                }`}>
                  {reachColor === 'green' ? 'On track' : reachColor === 'yellow' ? 'Needs work' : reachColor === 'red' ? 'At risk' : reachStatus}
                </span>
              </div>
            </div>
            <div className="z-10 mt-1 flex-1">
              <h3 className="font-semibold font-cabinet text-[var(--foreground)] text-base">
                Reach
              </h3>
              <p className="text-xs text-[var(--muted-foreground)] mt-1 font-ibm leading-relaxed line-clamp-2">
                {reachText}
              </p>
              
              {/* Reach Preview Data */}
              {reachAnalysis?.expected_reach_range && (
                 <div className="mt-3 text-[10px] font-medium bg-secondary/50 p-2 rounded-lg border border-border/50 flex flex-col gap-0.5">
                    <span className="text-muted-foreground uppercase tracking-wider text-[9px]">Est. Reach</span>
                    <span className="text-foreground font-semibold">
                        {(reachAnalysis.expected_reach_range.low / 1000).toFixed(0)}k - {(reachAnalysis.expected_reach_range.high / 1000).toFixed(0)}k views
                    </span>
                 </div>
              )}
            </div>
          </Link>
          </div>

          {/* Card 2: Brand Fit */}
          <div className="relative h-full group">
            <Link to={`/projects/${projectId}/brand-fit`} className="block relative p-5 pt-6 bg-[var(--card)] border border-[var(--border)] rounded-2xl hover:border-[var(--primary)]/50 hover:shadow-lg transition-all text-left flex flex-col gap-3 overflow-hidden h-full">
              <div className="flex justify-between items-start z-10 w-full">
              <div className="p-2 rounded-lg bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)] shadow-sm">
                <Target className="w-4.5 h-4.5" />
              </div>
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                  brandFitColor === 'green' ? 'bg-green-500/10 border-green-500/20' :
                  brandFitColor === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20' :
                  brandFitColor === 'red' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-gray-500/10 border-gray-500/20'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                    brandFitColor === 'green' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' :
                    brandFitColor === 'yellow' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]' :
                    brandFitColor === 'red' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' :
                    'bg-gray-500'
                }`}></div>
                <span className={`text-[10px] font-bold uppercase tracking-wide ${
                    brandFitColor === 'green' ? 'text-green-700 dark:text-green-400' :
                    brandFitColor === 'yellow' ? 'text-yellow-700 dark:text-yellow-400' :
                    brandFitColor === 'red' ? 'text-red-700 dark:text-red-400' :
                    'text-gray-700 dark:text-gray-400'
                }`}>
                  {brandFitColor === 'green' ? 'On track' : brandFitColor === 'yellow' ? 'Needs work' : brandFitColor === 'red' ? 'At risk' : brandFitStatus}
                </span>
              </div>
            </div>
            <div className="z-10 mt-1 flex-1">
              <h3 className="font-semibold font-cabinet text-[var(--foreground)] text-base">
                Brand Fit
              </h3>
              <p className="text-xs text-[var(--muted-foreground)] mt-1 font-ibm leading-relaxed line-clamp-2">
                {brandFitText}
              </p>

              {/* Brand Fit Preview Data */}
              {brandFitAnalysis?.category_mapping?.primary_categories && brandFitAnalysis.category_mapping.primary_categories.length > 0 && (
                 <div className="mt-3 text-[10px] font-medium bg-secondary/50 p-2 rounded-lg border border-border/50 flex flex-col gap-0.5">
                    <span className="text-muted-foreground uppercase tracking-wider text-[9px]">Top Categories</span>
                    <span className="text-foreground font-semibold truncate">
                        {brandFitAnalysis.category_mapping.primary_categories.slice(0, 2).join(", ")}
                    </span>
                 </div>
              )}
            </div>
          </Link>
          </div>

          {/* Card 3: Production */}
          <div className="relative h-full group">
            <Link to={`/projects/${projectId}/production-readiness`} className="block relative p-5 pt-6 bg-[var(--card)] border border-[var(--border)] rounded-2xl hover:border-[var(--primary)]/50 hover:shadow-lg transition-all text-left flex flex-col gap-3 overflow-hidden h-full">
              <div className="flex justify-between items-start z-10 w-full">
              <div className="p-2 rounded-lg bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)] shadow-sm">
                <Film className="w-4.5 h-4.5" />
              </div>
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                  prodReadinessColor === 'green' ? 'bg-green-500/10 border-green-500/20' :
                  prodReadinessColor === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20' :
                  prodReadinessColor === 'red' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-gray-500/10 border-gray-500/20'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                    prodReadinessColor === 'green' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' :
                    prodReadinessColor === 'yellow' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]' :
                    prodReadinessColor === 'red' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' :
                    'bg-gray-500'
                }`}></div>
                <span className={`text-[10px] font-bold uppercase tracking-wide ${
                    prodReadinessColor === 'green' ? 'text-green-700 dark:text-green-400' :
                    prodReadinessColor === 'yellow' ? 'text-yellow-700 dark:text-yellow-400' :
                    prodReadinessColor === 'red' ? 'text-red-700 dark:text-red-400' :
                    'text-gray-700 dark:text-gray-400'
                }`}>
                  {prodReadinessColor === 'green' ? 'On track' : prodReadinessColor === 'yellow' ? 'Needs work' : prodReadinessColor === 'red' ? 'At risk' : prodReadinessStatus}
                </span>
              </div>
            </div>
            <div className="z-10 mt-1 flex-1">
              <h3 className="font-semibold font-cabinet text-[var(--foreground)] text-base">
                Readiness
              </h3>
              <p className="text-xs text-[var(--muted-foreground)] mt-1 font-ibm leading-relaxed line-clamp-2">
                {prodReadinessText}
              </p>

              {/* Production Preview Data */}
              {prodReadinessAnalysis?.craft_assessment?.overall_craft_level && (
                 <div className="mt-3 text-[10px] font-medium bg-secondary/50 p-2 rounded-lg border border-border/50 flex flex-col gap-0.5">
                    <span className="text-muted-foreground uppercase tracking-wider text-[9px]">Script Craft</span>
                    <span className="text-foreground font-semibold capitalize">
                        {prodReadinessAnalysis.craft_assessment.overall_craft_level.replace(/_/g, " ")}
                    </span>
                 </div>
              )}
            </div>
            </Link>
          </div>
        </div>

        {/* CTAs */}
        <div className="w-full flex flex-col md:flex-row gap-4 items-center justify-between animate-slide-up delay-300">
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            {isOwner && (
              <button
                  className="group w-full md:w-auto py-3 px-6 bg-[var(--foreground)] text-[var(--background)] rounded-xl font-semibold font-cabinet text-base hover:shadow-2xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2.5 relative overflow-hidden"
                  onClick={() => navigate(`/projects/${projectId}/pitch-deck`)}
              >
                <Sparkles className="w-4.5 h-4.5" />
                Generate Pitch Deck
              </button>
            )}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="flex-shrink-0">
                <ShareScoreButton projectId={projectId!} isOwner={isOwner} />
              </div>
              {isOwner && (
                <button
                    className="group py-3 px-4 bg-transparent border border-[var(--border)] text-[var(--foreground)] rounded-xl hover:bg-[var(--secondary)] hover:border-[var(--primary)]/30 transition-all flex items-center justify-center"
                    title="Recalculate"
                    onClick={async () => {
                       await refetchScore();
                       // Invalidate project query to update logline and status
                       queryClient.invalidateQueries({ queryKey: ["project", projectId] });
                       showSuccess("Refreshed score data");
                    }}
                >
                  <RotateCw className="w-4.5 h-4.5 transition-transform group-hover:rotate-180" />
                </button>
              )}
            </div>
          </div>

          <span className="text-[11px] text-[var(--muted-foreground)] font-ibm bg-[var(--secondary)] px-3 py-1.5 rounded-lg border border-[var(--border)]">
            Last updated: <span className="font-semibold text-[var(--foreground)]">
              {artieScore?.created_at ? format(new Date(artieScore.created_at), "MMM d, h:mm a") : "Never"}
            </span>
          </span>
        </div>

        {/* Recommendations Section */}
        {artieScore && (
          <div className="mt-12">
            <RecommendationsSection
              scoreData={artieScore}
              projectId={projectId!}
              onUpdate={refreshScore}
            />
          </div>
        )}
    </>
  );
}

function formatStatus(status: string) {
    if (!status) return 'Unknown';
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function ProjectDetailsSkeleton() {
  return (
    <div className="w-full space-y-8">
      {/* Hero Grid Skeleton */}
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Skeleton className="md:col-span-2 h-[216px] rounded-2xl" />
        <Skeleton className="md:col-span-1 h-[216px] rounded-2xl" />
      </div>

      {/* Traffic Light Cards Skeleton */}
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Skeleton className="h-[200px] rounded-2xl" />
        <Skeleton className="h-[200px] rounded-2xl" />
        <Skeleton className="h-[200px] rounded-2xl" />
      </div>

      {/* CTAs Skeleton */}
      <div className="w-full flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <Skeleton className="h-12 w-48 rounded-xl" />
          <Skeleton className="h-12 w-12 rounded-xl" />
        </div>
        <Skeleton className="h-8 w-48 rounded-lg" />
      </div>
    </div>
  );
}